// ════════════════════════════════════════════════════════════════════════
// ToastContext.tsx — Sistema global de notificaciones con Context API
// ════════════════════════════════════════════════════════════════════════
// CONCEPTO: Context API + useContext
// Context permite compartir datos entre componentes sin pasar props
// manualmente en cada nivel (prop drilling).
//
// Cuándo usar Context:
//   ✅ Datos globales: tema, idioma, usuario autenticado, notificaciones
//   ❌ Estado local de un componente (usa useState)
//   ❌ Estado de servidor (usa una librería como React Query)
//
// PATRÓN: Custom Context Hook
// Exportar useToast() en lugar de useContext(ToastContext) directamente
// provee mejor manejo de errores y una API más limpia.
// ════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';

// ── Tipos del toast ───────────────────────────────────────────────────
export interface ToastMessage {
  id: string;
  icon: ReactNode;   // Antes era un emoji (string) — ahora un ícono SVG (ej: <XCircleIcon />)
  title: string;
  subtitle: string;
  variant?: 'success' | 'error' | 'info';
}

interface ToastContextValue {
  toasts: ToastMessage[];
  showToast: (toast: Omit<ToastMessage, 'id'>) => void; // Omit<T,K>: no necesita el id, lo generamos
  dismissToast: (id: string) => void;
}

// ── Crear el contexto ─────────────────────────────────────────────────
// createContext<T>(defaultValue) — el defaultValue solo se usa cuando
// no hay un Provider en el árbol de componentes padre.
// undefined como default nos fuerza a verificar en el hook customizado.
const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// ── Provider component ────────────────────────────────────────────────
// El Provider es el componente que "provee" el contexto a sus hijos.
// Todos los componentes dentro de <ToastProvider> pueden acceder al contexto.
interface ToastProviderProps {
  children: ReactNode; // ReactNode: cualquier cosa que React puede renderizar
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((toast: Omit<ToastMessage, 'id'>): void => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const newToast: ToastMessage = { ...toast, id };

    setToasts((prev) => [...prev, newToast]);

    // Auto-dismiss después de 4.5 segundos
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4500);
  }, []);

  const dismissToast = useCallback((id: string): void => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // El value del Provider es el objeto que los consumidores recibirán
  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
}

// ── Custom hook para consumir el contexto ─────────────────────────────
// Patrón recomendado: nunca llamar useContext directamente en los componentes.
// Usar este hook garantiza que:
//   1. El componente está dentro del Provider (o lanza error claro)
//   2. La API del contexto puede cambiar sin tocar cada consumidor
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);

  // Guard clause — Error descriptivo si se usa fuera del Provider
  if (context === undefined) {
    throw new Error('useToast debe usarse dentro de <ToastProvider>');
  }

  return context;
}
